import React, { useState, useEffect } from 'react';
import { airtableService } from '../services/airtable';
import { formatToLocalTime, getMinutesFromNow, getCurrentUTCTime } from '../utils/timeUtils';

const StudentQueue = ({ user, currentSession, onLogout }) => {
  const [queuePosition, setQueuePosition] = useState(null);
  const [queueRecord, setQueueRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joinTime, setJoinTime] = useState(null);
  const [inQueue, setInQueue] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [queueReason, setQueueReason] = useState('');
  const [operatoryNumber, setOperatoryNumber] = useState('');

  useEffect(() => {
    checkQueueStatus();
    const interval = setInterval(checkQueueStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkQueueStatus = async () => {
    if (!user?.sessionId) return;

    try {
      // Get all active queue records (same as what Dr. Grant sees)
      const allQueueRecords = await airtableService.getQueueForSession(user.sessionId);
      
      // Find this student's record
      const myRecord = allQueueRecords.find(record => 
        record.fields['Student Name'] === user.name && 
        record.fields['Status'] !== 'completed'
      );

      if (myRecord) {
        setQueueRecord(myRecord);
        setInQueue(true);
        setJoinTime(myRecord.fields['Join Time']);
        
        // Calculate position among WAITING students only, sorted by Join Time
        const waitingStudents = allQueueRecords
          .filter(record => record.fields['Status'] === 'waiting')
          .sort((a, b) => new Date(a.fields['Join Time']) - new Date(b.fields['Join Time']));
        
        const myPosition = waitingStudents.findIndex(record => record.id === myRecord.id) + 1;
        
        // If student is not in waiting status, they don't have a queue position
        if (myRecord.fields['Status'] === 'waiting') {
          setQueuePosition(myPosition);
        } else {
          setQueuePosition(null); // Student is in_progress or other status
        }
        
        console.log('🎯 Student queue status update:', {
          studentName: user.name,
          status: myRecord.fields['Status'],
          position: myRecord.fields['Status'] === 'waiting' ? myPosition : 'N/A',
          totalWaiting: waitingStudents.length,
          totalInQueue: allQueueRecords.length
        });
        
      } else {
        setInQueue(false);
        setQueuePosition(null);
        setQueueRecord(null);
        setJoinTime(null);
      }
    } catch (error) {
      console.error('Error checking queue status:', error);
    }
  };

  const joinQueue = async () => {
    setLoading(true);
    setError('');

    try {
      const currentQueue = await airtableService.getQueueForSession(user.sessionId);
      const nextPosition = currentQueue.length + 1;
      const currentTime = getCurrentUTCTime();

      const queueData = {
        sessionId: user.sessionId,
        studentName: user.name,
        clinicCode: user.clinicCode,
        joinTime: currentTime,
        position: nextPosition,
        isEmergency: isEmergency,
        queueReason: queueReason.trim(),
        operatoryNumber: operatoryNumber.trim(),
        yearLevel: user.yearLevel || ''
      };

      const record = await airtableService.addToQueue(queueData);
      
      if (record) {
        setQueueRecord(record);
        setInQueue(true);
        setJoinTime(currentTime);
        setQueuePosition(nextPosition);
      } else {
        setError('Failed to join queue. Please try again.');
      }
    } catch (error) {
      console.error('Error joining queue:', error);
      setError('Unable to join queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    return formatToLocalTime(timeString);
  };

  const calculateWaitTime = () => {
    if (!joinTime) return '';
    const minutes = getMinutesFromNow(joinTime);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const getStatusColor = () => {
    if (!queueRecord) return 'var(--text-light)';
    
    switch (queueRecord.fields['Status']) {
      case 'waiting': return 'var(--primary-blue)';
      case 'in_progress': return 'var(--coral)';
      case 'completed': return 'var(--muted-sage)';
      default: return 'var(--text-light)';
    }
  };

  const getStatusText = () => {
    if (!queueRecord) return 'Not in queue';
    
    switch (queueRecord.fields['Status']) {
      case 'waiting': return 'Waiting in queue';
      case 'in_progress': return 'Dr. Grant is with you';
      case 'completed': return 'Session completed';
      default: return 'Unknown status';
    }
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Student Queue</h1>
        <p>Welcome, {user.name}</p>
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <h3 style={{ color: 'var(--text-dark)', marginBottom: '4px' }}>
              Clinic: {user.clinicCode}
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
              Access Code: {user.accessCode}
            </p>
          </div>
          <button 
            onClick={onLogout} 
            className="btn btn-secondary"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            Logout
          </button>
        </div>

        {error && (
          <div style={{ 
            color: 'var(--coral)', 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#ffe6e6', 
            borderRadius: '6px' 
          }}>
            {error}
          </div>
        )}

        {!inQueue ? (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: 'var(--text-dark)', marginBottom: '16px' }}>
              Ready to see Dr. Grant?
            </h2>
            <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
              Click the button below to join the queue and Dr. Grant will be notified.
            </p>
            
            {/* Operatory Number */}
            <div className="input-group" style={{ textAlign: 'left', marginBottom: '16px' }}>
              <label htmlFor="operatoryNumber" style={{ fontWeight: 'bold' }}>
                Operatory Number
              </label>
              <input
                type="text"
                id="operatoryNumber"
                value={operatoryNumber}
                onChange={(e) => setOperatoryNumber(e.target.value)}
                placeholder="e.g. 12"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* Queue Reason */}
            <div className="input-group" style={{ textAlign: 'left', marginBottom: '16px' }}>
              <label htmlFor="queueReason" style={{ fontWeight: 'bold' }}>
                Reason for Visit
              </label>
              <input
                type="text"
                id="queueReason"
                value={queueReason}
                onChange={(e) => setQueueReason(e.target.value)}
                placeholder="e.g. Notes check, probing, sealant"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* Emergency Toggle */}
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: isEmergency ? '#ffe6e6' : 'var(--warm-cream)',
              borderRadius: '8px',
              border: isEmergency ? '2px solid var(--coral)' : '2px solid var(--light-blue)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                <input
                  type="checkbox"
                  checked={isEmergency}
                  onChange={(e) => setIsEmergency(e.target.checked)}
                  style={{
                    marginRight: '12px',
                    transform: 'scale(1.5)'
                  }}
                />
                🚨 This is an emergency
              </label>
              <p style={{
                color: 'var(--text-light)',
                fontSize: '14px',
                margin: '8px 0 0 32px'
              }}>
                {isEmergency ?
                  'You will be prioritized in the queue.' :
                  'Check this box if you have an urgent medical need.'
                }
              </p>
            </div>
            
            <button 
              onClick={joinQueue} 
              className="btn btn-primary"
              disabled={loading}
              style={{ 
                fontSize: '18px', 
                padding: '16px 32px',
                backgroundColor: isEmergency ? 'var(--coral)' : 'var(--primary-blue)'
              }}
            >
              {loading ? 'Joining...' : (isEmergency ? 'Join Emergency Queue' : 'Join Queue')}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              backgroundColor: 'var(--warm-cream)', 
              padding: '24px', 
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <h2 style={{ 
                color: getStatusColor(), 
                marginBottom: '12px',
                fontSize: '24px'
              }}>
                {getStatusText()}
              </h2>
              
              {queueRecord?.fields['Status'] === 'waiting' && queuePosition && (
                <>
                  <div style={{ 
                    fontSize: '48px', 
                    fontWeight: 'bold', 
                    color: 'var(--primary-blue)',
                    marginBottom: '8px'
                  }}>
                    #{queuePosition}
                  </div>
                  <p style={{ color: 'var(--text-dark)', fontSize: '18px' }}>
                    Your position in queue
                  </p>
                </>
              )}
              
              {queueRecord?.fields['Status'] === 'in_progress' && (
                <div style={{ 
                  fontSize: '24px', 
                  color: 'var(--coral)',
                  marginBottom: '8px'
                }}>
                  🏥 Dr. Grant is with you now
                </div>
              )}
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: 'white', 
                borderRadius: '8px',
                border: '2px solid var(--light-blue)'
              }}>
                <h4 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>
                  Joined Queue
                </h4>
                <p style={{ color: 'var(--text-light)' }}>
                  {formatTime(joinTime)}
                </p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: 'white', 
                borderRadius: '8px',
                border: '2px solid var(--peach)'
              }}>
                <h4 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>
                  Wait Time
                </h4>
                <p style={{ color: 'var(--text-light)' }}>
                  {calculateWaitTime()}
                </p>
              </div>
            </div>

            {queueRecord?.fields['Status'] === 'waiting' && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#e8f4f8', 
                borderRadius: '8px',
                border: '1px solid var(--light-blue)'
              }}>
                <p style={{ color: 'var(--text-dark)', margin: 0 }}>
                  🔄 Status updates automatically. Dr. Grant can see you're waiting.
                </p>
              </div>
            )}

            {queueRecord?.fields['Status'] === 'completed' && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#e8f5e8', 
                borderRadius: '8px',
                border: '1px solid var(--muted-sage)'
              }}>
                <p style={{ color: 'var(--text-dark)', margin: 0 }}>
                  ✅ Your session with Dr. Grant has been completed.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentQueue;